import React, { useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, ArrowRight, CheckCircle2, MessageCircle, Check, HelpCircle } from "lucide-react";
import { Reveal } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { useToast } from "../hooks/use-toast";
import { company } from "../mock";
import { getAttribution } from "../lib/attribution";
import { publicApi } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import BookCallButton from "../components/BookCallButton";
import xeroPlatinumCapsule from "../assets/xero-platinum-capsule.webp";
import Seo, { faqSchema } from "../components/Seo";
import MarketingConsent from "../components/MarketingConsent";
import LeadSourceField from "../components/LeadSourceField";
import { emptyLeadSource, leadSourceError, leadSourcePayload } from "../lib/leadSource";

const OTHER_OPTION = "Other";
const MEETING_ROOM_OPTION = "Meeting Room Hire";
const EXTRA_OPTIONS = [MEETING_ROOM_OPTION, OTHER_OPTION];

const SOFTWARE_OPTIONS = ["Xero", "Sage", "QuickBooks", "FreeAgent", "Excel", "Other", "Not currently using accounting software"];
const CONTACT_METHODS = ["Telephone", "Email", "Either"];

const normalise = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const findServiceOption = (raw, options) => {
  if (!raw) return null;
  const r = normalise(raw);
  return (
    options.find((o) => normalise(o) === r) ||
    options.find((o) => r.includes(normalise(o)) || normalise(o).includes(r)) ||
    null
  );
};

const emptyForm = { name: "", business_name: "", email: "", phone: "", website_url: "", message: "", current_software: "", contact_method: "", marketing_consent: false };

export default function Contact() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [website, setWebsite] = useState(""); // honeypot
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serviceError, setServiceError] = useState(false);
  const [leadSource, setLeadSource] = useState(emptyLeadSource);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceError, setSourceError] = useState("");
  const mountRef = useRef(Date.now());

  useEffect(() => {
    publicApi.services().then(setServices).catch(() => setServices([]));
    publicApi.serviceGroups().then(setServiceGroups).catch(() => setServiceGroups([]));
    publicApi.faqs().then((all) => setFaqs((all || []).slice(0, 8))).catch(() => setFaqs([]));
  }, []);

  // Group services into columns by their CMS group, mirroring the Header mega-menu.
  const groupedServices = (() => {
    const groups = [...serviceGroups].sort((a, b) => (a.order || 0) - (b.order || 0));
    const cols = groups.map((g) => ({ ...g, items: services.filter((s) => s.group === g.key) }));
    const known = new Set(groups.map((g) => g.key));
    const orphans = services.filter((s) => !known.has(s.group));
    if (orphans.length) cols.push({ key: "_other", label: "More", items: orphans });
    return cols.filter((c) => c.items.length > 0);
  })();

  // Flat list of all selectable service titles (incl. "Other") for matching/validation.
  const allServiceTitles = [...services.map((s) => s.title), ...EXTRA_OPTIONS];

  // Pre-tick the service when arriving from a service page (?service=Title)
  useEffect(() => {
    if (!services.length) return;
    const match = findServiceOption(searchParams.get("service"), allServiceTitles);
    if (match) setSelected((prev) => (prev.includes(match) ? prev : [...prev, match]));
  }, [searchParams, services]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (title) => { setServiceError(false); setSelected((prev) => (prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title])); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) {
      setServiceError(true);
      toast({ title: "Please select at least one service", description: "Let us know which service(s) you're interested in.", variant: "destructive" });
      document.querySelector("[data-testid='services-required']")?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    // Optional, so the biggest source of enquiries isn't slowed down. Only the
    // follow-up box on "Other" has to be filled in once something is chosen.
    const sourceMsg = leadSourceError(leadSource, sourceOptions, false);
    setSourceError(sourceMsg);
    if (sourceMsg) {
      document.querySelector('[data-testid="contact-lead-source-select"]')?.focus();
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.contact({ ...form, services: selected, website, page_url: window.location.pathname + window.location.search, attribution: getAttribution(), lead_source: leadSourcePayload(leadSource), elapsed_ms: Date.now() - mountRef.current });
      trackEvent("generate_lead", { form_type: "contact", services: selected.join(",") }, "contact_form_submit");
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setForm(emptyForm);
      setSelected([]);
      setLeadSource(emptyLeadSource);
    } catch (err) {
      const msg = err?.response?.status === 429 ? "Too many submissions. Please try again in a little while." : "Please try again or call us directly.";
      toast({ title: "Something went wrong", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const contactItems = [
    { icon: Phone, label: "Call us", value: company.phone, href: `tel:${company.phone}` },
    { icon: Mail, label: "Email us", value: company.email, href: `mailto:${company.email}` },
    { icon: Clock, label: "Opening hours", value: company.hours },
    { icon: MapPin, label: "Find us", value: company.address },
  ];

  return (
    <div>
      <Seo
        title="Let's Talk About Your Business"
        description="Talk to CASS about outsourced finance, Virtual FD, bookkeeping, payroll, VAT, financial reports, cash flow forecasting or Xero support. Serving Tamworth, Lichfield, Sutton Coldfield, Birmingham, Solihull, Burton upon Trent and the wider UK — no pressure, no obligation."
        jsonLd={faqs.length ? faqSchema(faqs) : undefined}
      />

      {/* Hero */}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <span className="inline-flex items-center gap-2 text-orange font-bold text-sm tracking-widest uppercase"><MessageCircle size={16} /> Get in touch</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">Let's Talk About<br />Your Business</h1>
          <div className="mt-6 text-slate-300 leading-relaxed space-y-4 max-w-2xl mx-auto text-left md:text-center">
            <p className="font-semibold text-white">Every great working relationship starts with a conversation.</p>
            <p>Tell us a little about your business and what you're looking to achieve. Once we've received your enquiry, we'll arrange a convenient time to talk through your requirements, answer your questions and provide a tailored recommendation that's right for your business.</p>
            <p>If it's the price you're after, you don't need to wait for us — our pricing is published. Build your package on the <Link to="/pricing" className="font-semibold text-white underline decoration-orange/60 hover:decoration-orange">pricing page</Link> and you'll see your own monthly fee and any one-off costs in about two minutes, without giving us your email address.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        {submitted ? (
          <div className="max-w-2xl mx-auto px-6">
            <div className="bg-slate-50 rounded-3xl p-10 md:p-14 border border-slate-100 text-center" data-testid="contact-thank-you">
              <CheckCircle2 className="text-orange mx-auto mb-6" size={64} />
              <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy mb-4">Thank You</h2>
              <div className="text-slate-500 leading-relaxed space-y-3 max-w-lg mx-auto">
                <p>Thanks for getting in touch.</p>
                <p>We'll review your enquiry and be in touch shortly to arrange a convenient time to talk about your business.</p>
                <p>In the meantime, feel free to explore our services and resources.</p>
              </div>
              <Link to="/" data-testid="thank-you-home" className="mt-8 inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">Return to Homepage <ArrowRight size={17} /></Link>
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1.5fr] gap-12">
            <Reveal>
              <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-3">Prefer to reach us directly?</h2>
              <p className="text-slate-500 mb-8">However you'd like to get in touch, we'd love to hear from you.</p>
              <div className="space-y-4">
                {contactItems.map((c) => {
                  const Icon = c.icon;
                  const inner = (
                    <div className="flex items-start gap-4 bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-orange/40 transition-colors">
                      <span className="w-12 h-12 rounded-xl bg-orange/10 flex items-center justify-center text-orange shrink-0"><Icon size={22} /></span>
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{c.label}</p>
                        <p className="font-display font-bold text-navy mt-0.5">{c.value}</p>
                      </div>
                    </div>
                  );
                  return c.href ? <a key={c.label} href={c.href} className="block">{inner}</a> : <div key={c.label}>{inner}</div>;
                })}
              </div>
              <div className="inline-flex bg-white rounded-2xl p-5 mt-6 border border-slate-100 shadow-sm">
                <img loading="lazy" decoding="async" src={xeroPlatinumCapsule} alt="Xero Platinum Partner" className="h-12 w-auto" />
              </div>

              <div className="mt-8 bg-navy rounded-2xl p-6 text-white" data-testid="contact-book-call-panel">
                <p className="font-display font-bold text-lg">Prefer to book a time?</p>
                <p className="text-slate-300 text-sm mt-1 mb-4">Grab a free, no-obligation discovery call at a time that suits you.</p>
                <BookCallButton testId="contact-book-call" label="Book a Call" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-6 py-3 rounded-full transition-all hover:-translate-y-0.5" />
              </div>

              <div className="mt-6 rounded-2xl overflow-hidden border border-slate-100 shadow-sm" data-testid="contact-map">
                <iframe
                  title="CASS office location"
                  src="https://maps.google.com/maps?q=3%20Pebble%20Close%2C%20Amington%2C%20Tamworth%2C%20Staffordshire%20B77%204RD&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-64 border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="bg-slate-50 rounded-3xl p-7 md:p-10 border border-slate-100">
                <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mb-1">Tell Us About Your Business</h2>
                <p className="text-slate-500 text-sm mb-7">Fields marked * are required.</p>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-navy font-semibold">Full Name *</Label>
                      <Input id="name" data-testid="field-name" required value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Jane Smith" className="mt-2 bg-white" />
                    </div>
                    <div>
                      <Label htmlFor="business_name" className="text-navy font-semibold">Business Name</Label>
                      <Input id="business_name" data-testid="field-business" value={form.business_name} onChange={(e) => setField("business_name", e.target.value)} placeholder="Acme Ltd" className="mt-2 bg-white" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="email" className="text-navy font-semibold">Email Address *</Label>
                      <Input id="email" data-testid="field-email" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="jane@company.co.uk" className="mt-2 bg-white" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-navy font-semibold">Telephone Number</Label>
                      <Input id="phone" data-testid="field-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="07000 000000" className="mt-2 bg-white" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="website_url" className="text-navy font-semibold">Website <span className="font-normal text-slate-400">(optional)</span></Label>
                    <Input id="website_url" data-testid="field-website" value={form.website_url} onChange={(e) => setField("website_url", e.target.value)} placeholder="www.yourbusiness.co.uk" className="mt-2 bg-white" />
                  </div>

                  <div data-testid="services-required">
                    <Label className="text-navy font-semibold">Services Required *</Label>
                    <p className={`text-xs mt-0.5 mb-3 ${serviceError ? "text-red-500 font-semibold" : "text-slate-400"}`}>{serviceError ? "Please select at least one service." : "Select all that apply (at least one required)."}</p>
                    <div className={`grid sm:grid-cols-2 gap-x-6 gap-y-6 ${serviceError ? "ring-2 ring-red-300 rounded-2xl p-2 -m-2" : ""}`}>
                      {groupedServices.map((col, ci) => {
                        const isLast = ci === groupedServices.length - 1;
                        const items = [...col.items.map((s) => s.title), ...(isLast ? EXTRA_OPTIONS : [])];
                        return (
                          <div key={col.key} className="space-y-2.5">
                            <p className="text-xs font-bold uppercase tracking-wider text-orange">{col.label}</p>
                            {items.map((title) => {
                              const active = selected.includes(title);
                              return (
                                <label key={title} data-testid={`service-${title}`}
                                  className={`flex items-center gap-3 text-left rounded-xl border px-4 py-3 cursor-pointer transition-colors ${active ? "border-orange bg-orange/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                                  <input type="checkbox" className="sr-only" checked={active} onChange={() => toggleService(title)} />
                                  <span className={`w-5 h-5 rounded-[6px] border flex items-center justify-center shrink-0 transition-colors ${active ? "bg-orange border-orange text-white" : "border-slate-300 bg-white"}`}>
                                    {active && <Check size={13} strokeWidth={3} />}
                                  </span>
                                  <span className="text-sm font-medium text-navy">{title}</span>
                                </label>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-navy font-semibold">Tell us about your business *</Label>
                    <Textarea id="message" data-testid="field-message" required value={form.message} onChange={(e) => setField("message", e.target.value)} rows={6} className="mt-2 bg-white"
                      placeholder={"We'd love to know a little more. For example:\n• What your business does\n• What you'd like help with\n• Any current challenges\n• Anything else you'd like us to know"} />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-navy font-semibold">Current Accounting Software</Label>
                      <Select value={form.current_software} onValueChange={(v) => setField("current_software", v)}>
                        <SelectTrigger data-testid="field-software" className="mt-2 bg-white"><SelectValue placeholder="Please choose..." /></SelectTrigger>
                        <SelectContent>{SOFTWARE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-navy font-semibold">Preferred Contact Method</Label>
                      <Select value={form.contact_method} onValueChange={(v) => setField("contact_method", v)}>
                        <SelectTrigger data-testid="field-contact-method" className="mt-2 bg-white"><SelectValue placeholder="Please choose..." /></SelectTrigger>
                        <SelectContent>{CONTACT_METHODS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Honeypot */}
                  <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true">
                    <label>Do not fill this field<input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
                  </div>

                  <LeadSourceField
                    value={leadSource}
                    onChange={(v) => { setLeadSource(v); setSourceError(""); }}
                    onOptions={setSourceOptions}
                    error={sourceError}
                    testId="contact-lead-source"
                  />

                  <MarketingConsent checked={form.marketing_consent} onChange={(v) => setField("marketing_consent", v)} testId="contact-marketing-consent" />

                  <Button type="submit" disabled={submitting} data-testid="contact-submit" className="w-full bg-orange hover:bg-orange-dark text-white font-bold rounded-full py-6 text-base">
                    {submitting ? "Sending..." : (<>Let's Talk <ArrowRight size={17} className="ml-2" /></>)}
                  </Button>
                </form>
              </div>
            </Reveal>
          </div>
        )}
      </section>

      {faqs.length > 0 && (
        <section className="py-16 md:py-20 bg-slate-50" data-testid="contact-faq-section">
          <div className="max-w-4xl mx-auto px-6">
            <Reveal>
              <div className="flex items-center gap-3 mb-8">
                <span className="w-11 h-11 rounded-xl bg-orange/10 flex items-center justify-center text-orange"><HelpCircle size={22} /></span>
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-orange">Before you get in touch</span>
                  <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy leading-tight">Frequently Asked Questions</h2>
                </div>
              </div>
              <Accordion type="single" collapsible className="space-y-3" data-testid="contact-faqs">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border border-slate-100 px-5">
                    <AccordionTrigger className="text-left font-display font-bold text-navy hover:no-underline">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-slate-600 leading-relaxed whitespace-pre-line">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>
      )}
    </div>
  );
}
